import { useCallback, useEffect, useMemo, useState, memo } from 'react';
import { useAppDispatch, useAppSelector } from '../../redux';
import type { AllTrailsResult } from '../../types/Results';
import { trailheadMarkerOffset } from '../../utils/constants';
import { updateClickedCoordinates } from '../../redux/reducer';
import MarkerCarousel from '../MarkerCarousel/MarkerCarousel';

type Props = {
  currentResult?: AllTrailsResult;
};

const TrailheadMarker = ({ currentResult }: Props) => {
  const dispatch = useAppDispatch();

  const { trailheadResults, clickedResult } = useAppSelector(state => ({
    trailheadResults: state.map.trailheadResults,
    clickedResult: state.map.clickedResult
  }));
  const [currentIndex, setCurrentIndex] = useState(0);
  const totalResultSlides = useMemo(() => (trailheadResults ? trailheadResults.length : 0), [trailheadResults]);

  const isTrackStyle = useMemo(() => {
    return !!(trailheadResults && currentResult?.type && currentResult?.type === 'track');
  }, [currentResult?.type, trailheadResults]);

  useEffect(
    () =>
      trailheadResults && currentResult && trailheadResults.indexOf(currentResult.ID) >= 0
        ? setCurrentIndex(trailheadResults.indexOf(currentResult.ID))
        : setCurrentIndex(0),
    [currentResult, trailheadResults]
  );

  const getCoordinates = (type: string, result?: AllTrailsResult) => {
    if (result) {
      if (result?._cluster_geoloc) {
        return result?._cluster_geoloc[type];
      }

      return result?._geoloc?.[type];
    }
    return null;
  };

  const clusterLongitude = getCoordinates('lng', currentResult);
  const clusterLatitude = getCoordinates('lat', currentResult);

  const normalizeIndex = useCallback(
    (newIndex: number) => {
      if (newIndex >= totalResultSlides) {
        return newIndex - totalResultSlides;
      }
      if (newIndex < 0) {
        return newIndex + totalResultSlides;
      }
      return newIndex;
    },
    [totalResultSlides]
  );

  const handleNavigate = useCallback(
    (newIndex: number, event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent) => {
      event?.stopPropagation();

      const index = normalizeIndex(newIndex);

      if (index !== currentIndex || !clickedResult) {
        dispatch(updateClickedCoordinates?.({ index }));
      }
    },
    [currentIndex, clickedResult, dispatch, normalizeIndex]
  );

  return (
    <MarkerCarousel
      currentIndex={currentIndex}
      resultCount={totalResultSlides}
      latitude={clusterLatitude}
      longitude={clusterLongitude}
      onNavigate={handleNavigate}
      isTrackStyle={isTrackStyle}
      offset={trailheadMarkerOffset}
    />
  );
};

export default memo(TrailheadMarker);
